import { mix, ref, type TokenSet } from "../emit/layers.ts";
import { accentNames } from "../primitives/primitives.ts";

/**
 * § Semantic tokens, light. Only this layer and its dark twin are redefined per theme;
 * components reference these names and never a primitive.
 */
function accentTriples(theme: "light" | "dark"): TokenSet {
  const out: Record<string, ReturnType<typeof ref>> = {};
  for (const name of accentNames) {
    out[`color-accent-${name}-bg`] = ref(`${name}-3-${theme}`);
    out[`color-accent-${name}-solid`] = ref(`${name}-9`);
    out[`color-accent-${name}-on-solid`] = ref(`${name}-9-on`);
    out[`color-accent-${name}-text`] = ref(`${name}-11-${theme}`);
  }
  return out;
}

export const accentTriplesLight = accentTriples("light");
export const accentTriplesDark = accentTriples("dark");

/**
 * The Team accent (2026-09-29, by the owner's report: "these ui elements.. should match the
 * appearance color of the team set... not just be purple all the time"): the one colour of action,
 * current and selected, and the only accent a feature 024 palette rewrites
 * (`contracts/design-system.md` § Token architecture § What a per-workspace palette replaces).
 * Its static value is the blue ramp, the derived default a Team shows before its palette arrives;
 * the eight named accents above stay fixed, so a categorical or meaning blue never follows a Team.
 */
function teamAccent(theme: "light" | "dark"): TokenSet {
  return {
    "color-accent-bg": ref(`blue-3-${theme}`),
    "color-accent-solid": ref("blue-9"),
    "color-accent-on-solid": ref("blue-9-on"),
    "color-accent-text": ref(`blue-11-${theme}`),
    // The accent drawn as a line or an edge (an underline, the open row's bar, a drop line, a thin
    // progress fill, a stepper's rule, a checked radio's dot): the accent hue at a lightness that
    // reaches 3:1 on every neutral surface, which is what a palette serves as its focus ring.
    "color-accent-line": ref(`focus-${theme}`),
    // The edge every solid accent fill carries (a filled button, a switch, a checked box, a solid
    // badge, the selected day, the unread dot): transparent while the solid reaches 3:1 on every
    // background by itself, as the static blue does, and a palette's neutral outline where its solid
    // does not (024 FR-024-015, 2026-09-29).
    "color-accent-border": "transparent",
    // The tinted button's states (2026-10-03, by the owner's word on the milestone move button: "I
    // like the style of the milestone move button but perhaps it should have a color"): the accent
    // as a soft fill, derived from the tokens a palette rewrites so they follow every Team and both
    // themes. The rest fill is `color-accent-bg` under `color-accent-text`, the pair the palette
    // generator holds at 4.5:1 (024's `text_on_tint`); each state steps the fill toward the accent
    // line, and because a light palette's accent text sits close to 4.5:1 on its tint, any deeper
    // fill would take the label under it, so a hovered or pressed label moves 30 percent toward the
    // primary text, which keeps it at 4.5:1 or more on both fills for every stored palette
    // (`theme/tinted.test.ts`).
    "color-accent-bg-hover": mix("color-accent-line", 10, "color-accent-bg"),
    "color-accent-bg-active": mix("color-accent-line", 18, "color-accent-bg"),
    "color-accent-text-strong": mix("color-text-primary", 30, "color-accent-text"),
    // The tinted button's edge, the accent line softened into its fill: decorative, since the
    // label names the control and the focus ring marks it.
    "color-accent-tint-border": mix("color-accent-line", 40, "color-accent-bg"),
  };
}

export const teamAccentLight = teamAccent("light");
export const teamAccentDark = teamAccent("dark");

export const semanticLight: TokenSet = {
  "color-bg-app": ref("neutral-light-1"),
  "color-bg-subtle": ref("neutral-light-2"),
  "color-bg-surface": ref("white"),
  "color-bg-raised": ref("white"),
  // 2026-10-15, by the owner's decision on the composer surface (contracts/design-system.md § Composer): the composer's fill, white in light and one step off the dark sheet, so the box reads as raised by tone alone and casts no shadow.
  "composer-bg": ref("white"),
  // 2026-10-15: the block the person's message sits in on the assistant's thread (contracts/design-system.md § Assistant panel), the hover step in dark, where the subtle step is darker than the sheet and showed nothing.
  // 2026-10-20, the assistant panel's approved redesign (contracts/design-system.md § Assistant panel): the bubble moves to neutral 4, since the subtle step all but vanished on the work surface; the panel's raised surfaces (its cards, lists and composer) are white, one step off the sheet, and its hairlines inside a bordered block are neutral 5.
  "assistant-said-bg": ref("neutral-light-4"),
  "assistant-raised-bg": ref("white"),
  "assistant-hairline": ref("neutral-light-5"),
  // The sunk fill of a chip, a citation number and a source's kind mark, and the hover of a row on a raised surface: neutral 3 in light.
  "assistant-sunk-bg": ref("neutral-light-3"),
  // 2026-10-20, the assistant panel's redesign (contracts/design-system.md § Approval card): the red drawn as a soft edge, the strong card's border and the decline button's, the red solid softened into its tint as the tinted button's edge is; decorative, since the words carry the meaning.
  "color-accent-red-line": mix("color-accent-red-solid", 40, "color-accent-red-bg"),
  "color-bg-hover": ref("neutral-light-3"),
  "color-bg-active": ref("neutral-light-4"),
  "color-bg-selected": ref("blue-3-light"),
  "color-bg-inverse": ref("neutral-light-12"),
  // 2026-09-28, by the owner's pick from a sample swatch: the page behind the sign-in card, one grey in both themes, lighter than the card's sidebar chrome so the card reads as the darker object on it. It sits between the dark ramp's #272727 and #2e2e2e, and the owner's exact value is kept (contracts/design-system.md § Semantic tokens).
  "color-bg-backdrop": "#2a2a2a",
  "color-border-subtle": ref("neutral-light-6"),
  "color-border-default": ref("neutral-light-7"),
  "color-border-hover": ref("neutral-light-8"),
  "color-border-strong": ref("neutral-light-9"),
  "color-text-primary": ref("neutral-light-12"),
  // § Ink levels (2026-10-15, by the owner's decision): four named inks, each at 4.5:1 or more on every surface it is drawn on.
  // The secondary sits about halfway between the tertiary and the primary in contrast, at the light ramp's grey hue: text read
  // after the primary in the same block (an empty state's title, a tool row's title).
  "color-text-secondary": "#4f5761",
  // The tertiary is the secondary text of 2026-10-10 unchanged: one step under neutral 11 (#666f7a), the least darkening that
  // holds 4.5:1 on the selected row, which is the Team accent's tint: 4.54:1 on the static blue 3 and 4.51:1 or more on every
  // stored palette's light tint, where neutral 11 gave 4.47:1 on blue 3 and failed 75 of the 288 (contracts/design-system.md
  // § Semantic tokens). Neutral 11 itself stays, since the palette generator of 024 reads the ramp. Every reader of the
  // secondary before 2026-10-15 reads this, so no pixel moved on that day; it is the ink of metadata.
  "color-text-tertiary": "#656e79",
  // The lightest grey that reaches 4.5:1 on every fill an empty field is drawn on (4.62:1 on the sheet); neutral 10 sat at
  // 3.79:1 until 2026-10-15, on a ground SC 1.4.3 does not give.
  "color-text-placeholder": "#68717c",
  "color-text-disabled": ref("neutral-light-9"),
  "color-text-on-solid": ref("blue-9-on"),
  // § Meaning colours, the priority marks: one saturated colour per value for the flag every surface draws, chosen on 2026-09-17 by the owner's decision because the ramps' solid step reads pale as a small filled glyph and their text step reads muddy; the same four in both themes.
  "color-priority-urgent": "#dc3d3d",
  "color-priority-high": "#f28c28",
  "color-priority-medium": "#e0b000",
  "color-priority-low": "#3b82f6",
  "color-priority-none": ref("neutral-light-10"),
  "color-focus-ring": ref("focus-light"),
  "color-scrim": ref("scrim-light"),
  "color-edge-shade": ref("edge-shade-light"),
  "color-solid-hover-overlay": ref("solid-hover-overlay"),
  "color-solid-active-overlay": ref("solid-active-overlay"),
  ...teamAccentLight,
  ...accentTriplesLight,
  // § Chrome and work-surface tokens, the themed half.
  // 2026-09-13: measured from the owner's reference, .impeccable/references/shell-reference.png: the sheet and the pill tab track. The reference's lavender callout, which the empty state was drawn on, went on 2026-09-29 with the empty state's box (contracts/design-system.md § Empty).
  "color-work-surface": "#f7f7f7",
  "color-work-subtle": "#f7f8f9",
  "color-work-border": "#d6d9de",
  // 2026-09-23: a boundary on the sheet that carries meaning. `color-work-border` is decorative (1.32:1) and neutral 9 (--color-border-strong) falls to 2.89:1 on the #f7f7f7 sheet, below the 3:1 of SC 1.4.11, so the first ramp step darker than 9 that reaches it, neutral 10, is the work-sheet boundary at 3.63:1 (contracts/design-system.md § Chrome and work-surface tokens).
  "color-work-border-strong": ref("neutral-light-10"),
  // 2026-09-15: the one tint a row on the sheet takes, a group header's band and a hovered row, a step darker than the sheet (contracts/design-system.md § Chrome and work-surface tokens).
  "color-work-tint": ref("neutral-light-3"),
  "color-work-text": "#23272b",
  "color-work-text-secondary": ref("color-text-secondary"),
  "color-work-text-tertiary": ref("color-text-tertiary"),
  "color-tab-track": "#f5f5f5",
  "color-tab-track-border": "#eaeaea",
  "color-tab-active": "#ffffff",
  // § Elevation, per theme.
  // 2026-09-21 (the in-app file readers): the paper a PDF page is painted on. A page carries its own
  // colours and is white in both themes; tinting it would misreport the document.
  "color-pdf-page": "#ffffff",
  "elevation-0": "none",
  "elevation-1": ref("shadow-1-light"),
  "elevation-2": ref("shadow-2-light"),
  "elevation-3": ref("shadow-3-light"),
  "elevation-drag": ref("shadow-drag-light"),
  // 2026-09-29, by the owner's review of the board ("the dropshadow has to be darker"): the board card's surface, edge and elevation, one set for the task board and the client board (contracts/design-system.md § Board card tokens). In light the card is white on the column and lifts by its shadow; its 1 px edge is transparent, drawn only in forced colours.
  "color-card-surface": ref("white"),
  "color-card-border": "transparent",
  "color-card-border-hover": "transparent",
  "elevation-card": ref("shadow-card-light"),
  "elevation-card-hover": ref("shadow-card-hover-light"),
};
